import { useMemo } from "react";
import { useApiClient } from "../apiClients";
import { ENDPOINTS_PAIEMENT } from "./endpoints";

/**
 * Une ligne de facture : un bénéficiaire et sa période.
 *
 * Le montant n'est renseigné que pour CashPlus, et il est de toute façon
 * ignoré par le serveur, qui recalcule le prix. Seuls comptent le bénéficiaire
 * et la durée.
 */
type BillingDetailPayload = {
  student_id: number;
  start_date: string;
  end_date: string;
  amount: number;
};

/**
 * Un enfant du parent connecté, avec l'état de son abonnement.
 *
 * `deja_abonne` couvre l'actif ET le paiement en attente : dans les deux cas
 * il n'y a rien à payer maintenant, et le proposer ferait payer deux fois.
 */
export type TEnfantAPayer = {
  userapp_id: number;
  nom?: string;
  prenom?: string;
  login?: string;
  statut?: string;
  date_fin?: string;
  deja_abonne: boolean;
};

export type CashplusInitiatePayload = {
  billing: {
    request_id?: string;
    user_id?: number;
    generated_at?: string;
    expiration_date?: string;
    total_amount?: number;
    details: BillingDetailPayload[];
  };
  /** "parent" (défaut) ou "ecole". */
  mode?: ModePaiement;
  /**
   * Code de l'établissement. Il applique le tarif négocié de l'école — le
   * parent PAIE ce tarif, il n'obtient pas la gratuité.
   */
  code_ecole?: string;
  /**
   * Code de l'animateur qui a recommandé Tilmicode. Attribue les enfants du
   * panier à l'encaissement ; un code invalide ne bloque jamais le paiement.
   * Jamais avec un code d'école : le serveur refuse les deux ensemble.
   */
  code_apporteur?: string;
};

/** Ce que le serveur a fait du code animateur d'un paiement. */
export type TResultatCodeApporteur = {
  statut: 'applique' | 'inactif' | 'inconnu' | 'invalide' | 'auto_attribution' | 'ignore' | 'indisponible';
};

/**
 * Avantage animateur d'une ligne du paiement : code présenté (explicite) ou
 * celui de l'animateur auquel l'enfant est déjà attribué. Avec un motif, il ne
 * s'applique pas et la ligne garde le prix public.
 */
export type TAvantageApporteur = {
  beneficiaire_id: number;
  explicite: boolean;
  avantage_type: 'pourcentage' | 'montant_fixe' | 'prix_fixe';
  avantage_valeur?: number;
  devise: string;
  prix_public_mensuel: number;
  prix_applique_mensuel: number;
  motif?: 'devise_differente' | 'prix_nul';
};

export type CashplusInitiateResponse = {
  id_billing: number;
  request_id: string;
  cashplus_token: string;
  date_expiration: string;
  cashplus_token_expiration?: string; // ISO — expiration du token CashPlus (≤24 h)
  total_amount: number;
  currency?: string;
  code_apporteur?: TResultatCodeApporteur;
  avantages_apporteur?: TAvantageApporteur[];
};

// Le montant n'est PAS envoye par le client : le serveur le recalcule depuis la
// table pricing, dans la devise de la boutique. Seules les periodes comptent.
export type LemonsqueezyInitiatePayload = {
  billing: {
    request_id?: string;
    generated_at?: string;
    expiration_date?: string;
    details: Omit<BillingDetailPayload, 'amount'>[];
  };
  // Prerempli le formulaire de paiement, rien de plus : l'acheteur peut le
  // corriger sur la page Lemon Squeezy.
  customer?: { email?: string; name?: string; country?: string };
  /** "parent" (défaut) ou "ecole". */
  mode?: ModePaiement;
  /** Code de l'établissement : applique le tarif négocié, ne rend pas gratuit. */
  code_ecole?: string;
  /** Rythme de prélèvement. Vide = mensuel. */
  periodicite?: Periodicite;
  /** Code de l'animateur : voir le paiement CashPlus. */
  code_apporteur?: string;
};

export type LemonsqueezyInitiateResponse = {
  id_billing: number;
  request_id: string;
  checkout_id: string;
  // URL hebergee vers laquelle rediriger l'acheteur.
  checkout_url: string;
  checkout_expiration?: string;
  // Montant et devise qui font foi : c'est ce que le serveur a retenu.
  total_amount: number;
  currency: string;
  test_mode?: boolean;
  code_apporteur?: TResultatCodeApporteur;
  avantages_apporteur?: TAvantageApporteur[];
};

/**
 * Ce qu'un code d'etablissement vaut, avant tout paiement.
 * Ne porte ni l'identite de l'ecole ni ses conditions : seulement le prix.
 */
export type TCodeEcoleInfo = {
  valide: boolean;
  prix?: number;
  devise?: string;
  /**
   * L'établissement règle lui-même : le parent ne paie rien et aucun montant
   * n'est renvoyé. C'est ce drapeau qui fait basculer l'écran de paiement.
   */
  ecole_paie?: boolean;
};

/** Un siège ouvert que l'établissement n'a pas encore payé. */
export type TSiegeARegler = {
  id_billing: number;
  student_id: number;
  nom: string;
  debut: string;
  fin: string;
  montant: number;
  devise: string;
};

/** Un siège déjà payé, conservé en historique. */
export type TSiegeRegle = {
  id_billing: number;
  student_id: number;
  nom: string;
  montant: number;
  devise: string;
  regle_le?: string;
  vendor?: string;
};

/**
 * Un règlement lancé mais pas encore encaissé.
 *
 * Ses lignes sont retirées des sièges à régler — c'est ce qui empêche de payer
 * deux fois les mêmes élèves. Ce rappel dit à l'école où en est le premier
 * paiement, sans quoi elle verrait sa liste se vider sans comprendre.
 */
export type TReglementEnCours = {
  id_billing: number;
  total: number;
  devise: string;
  vendor: string;
  cashplus_token?: string;
  checkout_url?: string;
  expire?: string;
  lignes: number;
  /** Bénéficiaires couverts, pour marquer leur ligne « En attente ». */
  eleves?: number[];
};

/**
 * Un mois d'abonnement d'établissement (TilmiSchool).
 *
 * Quantité et prix sont figés à la génération : une facture présentée ne
 * change pas parce que l'effectif a bougé depuis.
 */
export type TEcheanceAbonnement = {
  id: number;
  organization_id: number;
  offre_code: string;
  /** Premier jour du mois couvert. */
  periode: string;
  quantite: number;
  prix_unitaire: number;
  montant: number;
  devise: string;
  regle_le?: string;
  vendor?: string;
  /** Pourquoi le montant est nul, quand il l'est. */
  gratuite_motif?: string;
};

/** Ce que l'établissement doit, et ce qu'il a déjà payé. */
export type TEtatReglement = {
  organization_id: number;
  /** Le code que l'établissement remet aux familles. Absent s'il n'a pas de contrat. */
  code_ecole?: string;
  devise: string;
  total_du: number;
  a_regler: TSiegeARegler[];
  regles: TSiegeRegle[];
  /** Règlement déjà lancé, non encaissé. Absent = aucun. */
  en_cours?: TReglementEnCours | null;
  /** Mois d'abonnement d'établissement encore dus (TilmiSchool). */
  abonnements?: TEcheanceAbonnement[];
  /** Ce que ces échéances représentent. */
  total_abonnements?: number;
};

/** Ce que l'écran doit faire après avoir lancé un règlement. */
export type TReglementResponse = {
  mode: string;
  total: number;
  devise: string;
  lignes: number;
  cashplus_token?: string;
  token_expiration?: string;
  checkout_url?: string;
  ids_billing?: number[];
};

/** Qui règle la facture. */
export type ModePaiement = "parent" | "ecole";

/** Rythme de prélèvement d'un abonnement par carte. */
export type Periodicite = "mensuelle" | "trimestrielle" | "annuelle";

/**
 * Un élève que l'établissement peut passer en premium : il n'a pas de siège en
 * cours. Alimente l'écran de sélection du mode « l'école paie ».
 */
export type TEleveSelectionnable = {
  student_id: number;
  userapp_id?: number;
  nom: string;
  prenom: string;
  massar?: string;
  /** Pas encore de compte Tilmicode : l'élève ne peut pas devenir premium. */
  sans_compte: boolean;
};

/**
 * Ce que le serveur rend après un arrêt ou une reprise de reconduction.
 *
 * `fin_acces` vient du prestataire : lui seul sait quand la période qu'il a
 * facturée s'achève. C'est LA date que le parent doit lire — celle qui lui
 * dit qu'il n'a rien perdu en arrêtant.
 */
export type TEtatReconduction = {
  beneficiaire_id: number;
  reconduit: boolean;
  fin_acces: string;
  prochain_prelevement?: string;
};

export const usePaiementApi = () => {
  const paiementClient = useApiClient("paiement");

  return useMemo(() => ({
    // Arrête la reconduction d'UN enfant. L'accès court jusqu'au terme déjà
    // payé : le serveur rend cette date, et l'écran doit l'afficher — sinon
    // le parent doute et fait opposition auprès de sa banque.
    arreterReconduction: async (beneficiaireId: number): Promise<TEtatReconduction> => {
      const { data } = await paiementClient.post<TEtatReconduction>(
        ENDPOINTS_PAIEMENT.ARRETER_RECONDUCTION(beneficiaireId),
      );
      return data;
    },
    // Rétablit la reconduction, sans aucun paiement : la période en cours
    // est déjà réglée. Impossible une fois cette période close.
    reprendreReconduction: async (beneficiaireId: number): Promise<TEtatReconduction> => {
      const { data } = await paiementClient.post<TEtatReconduction>(
        ENDPOINTS_PAIEMENT.REPRENDRE_RECONDUCTION(beneficiaireId),
      );
      return data;
    },
    getStatuses: (userIds: number[]) =>
      paiementClient.get(ENDPOINTS_PAIEMENT.GET_STATUS(userIds)),
    // Les enfants du parent connecté, avec l'état de chaque abonnement. Le
    // parent vient de la session : rien à passer, et rien à deviner côté
    // navigateur.
    listerMesEnfants: async (): Promise<TEnfantAPayer[]> => {
      const { data } = await paiementClient.get<{ enfants: TEnfantAPayer[] }>(
        ENDPOINTS_PAIEMENT.MES_ENFANTS
      );
      return data?.enfants ?? [];
    },
    // signupToken : paiement de fin de tunnel d'inscription. Le compte parent
    // n'étant pas encore activé, il n'y a pas de session — c'est le jeton qui
    // désigne le payeur et les comptes couverts.
    initiateCashplus: (payload: CashplusInitiatePayload, signupToken?: string) =>
      paiementClient.post<CashplusInitiateResponse>(
        ENDPOINTS_PAIEMENT.CASHPLUS.INITIATE,
        payload,
        signupToken ? { headers: { "X-Signup-Token": signupToken } } : undefined
      ),
    // Verifie un code d'etablissement et annonce son tarif AVANT que le parent
    // ne s'engage. Route ouverte : utilisable des la creation du compte, sans
    // session ni jeton.
    verifierCodeEcole: async (code: string): Promise<TCodeEcoleInfo> => {
      const { data } = await paiementClient.get<TCodeEcoleInfo>(
        ENDPOINTS_PAIEMENT.ECOLE.VERIFIER_CODE(code)
      );
      return data;
    },
    // Ce que l'établissement doit : une ligne par élève, jamais refacturée une
    // fois payée.
    etatReglement: async (): Promise<TEtatReglement> => {
      const { data } = await paiementClient.get<TEtatReglement>(ENDPOINTS_PAIEMENT.ECOLE.REGLEMENT);
      return data;
    },
    // Règle tout ou partie des sièges dus. Le montant est recalculé serveur :
    // on n'envoie que des identifiants de lignes.
    reglerSieges: async (
      idsBilling: number[],
      vendor: string,
      periodicite?: Periodicite
    ): Promise<TReglementResponse> => {
      const { data } = await paiementClient.post<TReglementResponse>(
        ENDPOINTS_PAIEMENT.ECOLE.REGLEMENT,
        { ids_billing: idsBilling, vendor, periodicite }
      );
      return data;
    },
    /**
     * Règle des mois d'abonnement d'établissement (TilmiSchool).
     *
     * Appel distinct de `reglerSieges` bien que la route soit la même : les
     * deux dettes ne datent pas la même table à l'encaissement, et mélanger
     * leurs identifiants rendrait impossible de savoir laquelle solder.
     */
    reglerAbonnement: async (
      idsEcheance: number[],
      vendor: string
    ): Promise<TReglementResponse> => {
      const { data } = await paiementClient.post<TReglementResponse>(
        ENDPOINTS_PAIEMENT.ECOLE.REGLEMENT,
        { ids_echeance: idsEcheance, vendor }
      );
      return data;
    },
    // Mode « l'école paie » : les élèves encore sans siège, que la direction
    // peut sélectionner. Le total à régler est calculé SERVEUR au paiement —
    // nombre de sélectionnés x prix négocié.
    listerElevesSelectionnables: async (orgID: number): Promise<TEleveSelectionnable[]> => {
      const { data } = await paiementClient.get<{ eleves: TEleveSelectionnable[] }>(
        ENDPOINTS_PAIEMENT.ECOLE.ELEVES_SELECTIONNABLES(orgID)
      );
      return data?.eleves ?? [];
    },
    initiateLemonsqueezy: (payload: LemonsqueezyInitiatePayload, signupToken?: string) =>
      paiementClient.post<LemonsqueezyInitiateResponse>(
        ENDPOINTS_PAIEMENT.LEMONSQUEEZY.INITIATE,
        payload,
        signupToken ? { headers: { "X-Signup-Token": signupToken } } : undefined
      ),
  }), [paiementClient]);
};
